import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { EarlyResearchPlan, getEarlyResearchPlan, PlanningStatus, ResearchMethodAssignment, saveEarlyResearchPlan } from '../api/marketResearchEarlyPlan';

interface Props {
  flowId?: string | null;
  viewOnly?: boolean;
  disabled?: boolean;
  refreshKey?: string | number;
}
const labels = { planned: 'Planned', in_progress: 'In progress', performed: 'Performed', inconclusive: 'Inconclusive', no_longer_relevant: 'No longer relevant' };
const assignments = (plan: EarlyResearchPlan): Record<string, ResearchMethodAssignment> => Object.fromEntries(plan.methods.map(method => [method.id, {
  id: method.id, owner: method.owner, role: method.role, due_date: method.due_date, acceptance_criterion: method.acceptance_criterion,
}]));

/** Optional planning details use the early-stage server projection for every audience. */
const MarketResearchEarlyPlan: React.FC<Props> = ({ flowId, viewOnly = false, disabled = false, refreshKey }) => {
  const [saved, setSaved] = useState<{ flowId: string; plan: EarlyResearchPlan } | null>(null);
  const [draft, setDraft] = useState<Record<string, ResearchMethodAssignment>>({});
  const [expanded, setExpanded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [newerPlan, setNewerPlan] = useState<EarlyResearchPlan | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const requestRef = useRef(0);
  const revisionRef = useRef<number | null>(null);
  const flowRef = useRef(flowId);
  const plan = saved && saved.flowId === flowId ? saved.plan : null;
  const canEdit = !!plan?.can_edit && !viewOnly && !disabled && !loading && !saving && !newerPlan;
  useEffect(() => {
    const request = ++requestRef.current;
    const changedFlow = flowRef.current !== flowId;
    flowRef.current = flowId;
    setSaving(false);
    if (changedFlow) {
      setSaved(null); revisionRef.current = null; setDraft({}); setExpanded(false); setDirty(false); dirtyRef.current = false; setNewerPlan(null); setMessage('');
    }
    if (!flowId) return;
    setLoading(true);
    getEarlyResearchPlan(flowId).then(next => {
      if (request !== requestRef.current) return;
      if (!next) { setSaved(null); return; }
      if (dirtyRef.current && !changedFlow && next.revision !== revisionRef.current) {
        setNewerPlan(next);
        setMessage('Saved research details changed. Your edits remain here; reload the saved plan before editing further.');
        return;
      }
      setSaved({ flowId, plan: next });
      revisionRef.current = next.revision;
      if (!dirtyRef.current) setDraft(assignments(next));
      setNewerPlan(null);
    }).catch(() => {
      if (request === requestRef.current) setMessage('The research plan could not be loaded. You may continue with the workflow.');
    }).finally(() => { if (request === requestRef.current) setLoading(false); });
    return () => { ++requestRef.current; };
  }, [flowId, refreshKey, reloadKey]);

  if (!flowId) return null;
  if (!plan) return message ? <Alert severity="info" role="status" sx={{ my: 1 }}>{message}<Button size="small" onClick={() => setReloadKey(value => value + 1)}>Try loading plan</Button></Alert> : null;
  const update = (id: string, field: keyof ResearchMethodAssignment, value: string) => {
    if (!canEdit) return;
    setDraft(current => ({ ...current, [id]: { ...current[id], [field]: value || null } }));
    dirtyRef.current = true; setDirty(true); setMessage('');
  };
  const save = async () => {
    if (!canEdit || !dirty) return;
    const previous = assignments(plan);
    const changes = Object.values(draft).filter(row => Object.entries(row).some(([key, value]) =>
      key !== 'id' && value !== (key === 'status'
        ? plan.methods.find(method => method.id === row.id)?.status
        : previous[row.id]?.[key as keyof ResearchMethodAssignment])));
    if (!changes.length) { dirtyRef.current = false; setDirty(false); return; }
    const request = ++requestRef.current;
    setSaving(true); setMessage('');
    try {
      const next = await saveEarlyResearchPlan(flowId, plan.revision, changes);
      if (request !== requestRef.current || flowRef.current !== flowId) return;
      setSaved({ flowId, plan: next }); setDraft(assignments(next)); dirtyRef.current = false; setDirty(false); setMessage('Research plan saved.');
      revisionRef.current = next.revision;
    } catch {
      if (request === requestRef.current) setMessage('The research plan could not be saved. Your edits remain here. Reload the saved plan if it changed, then try again.');
    } finally { if (request === requestRef.current) setSaving(false); }
  };
  return <Box component="section" aria-label="Early research plan" data-testid="market-research-early-plan" sx={{ border: 1, borderColor: 'divider', borderRadius: 1, my: 2, p: 1.5 }}>
    <Button size="small" aria-expanded={expanded} onClick={() => setExpanded(value => !value)}>Research plan (optional) · {plan.methods.length} methods</Button>
    <Typography variant="caption" display="block" color="text.secondary">Planning details are optional. Proposals do not mean performed research or an official decision.</Typography>
    {message && <Alert severity={message === 'Research plan saved.' ? 'success' : 'info'} sx={{ mt: 1 }} role="status">{message}</Alert>}
    {message.includes('could not be saved') && <Button size="small" onClick={() => setReloadKey(value => value + 1)}>Check saved plan</Button>}
    {newerPlan && <Button size="small" onClick={() => {
      setSaved({ flowId, plan: newerPlan }); setDraft(assignments(newerPlan)); setNewerPlan(null); setDirty(false); dirtyRef.current = false; setMessage('');
      revisionRef.current = newerPlan.revision;
    }}>Reload saved plan</Button>}
    {expanded && <Stack spacing={2} sx={{ mt: 1 }}>
      {plan.objectives.map((value, index) => <Typography variant="body2" key={`objective-${index}`}>{value}</Typography>)}
      {!!plan.unknowns.length && <Box><Typography variant="subtitle2">Open questions</Typography>{plan.unknowns.map((value, index) => <Typography variant="body2" key={`unknown-${index}`}>{value}</Typography>)}</Box>}
      {plan.methods.map(method => <Box key={method.id}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap"><Typography variant="subtitle2">{method.label}</Typography><Chip size="small" label={labels[method.status]} />{method.follow_up_required && <Chip size="small" color="warning" label="Follow-up required" />}</Stack>
        <Typography variant="caption" color="text.secondary">{method.assignment_origin === 'user_confirmed' ? 'Human-confirmed assignment' : 'Proposed method; owner and date may remain unknown'}</Typography>
        {method.expected_evidence && <Typography variant="body2" color="text.secondary">Expected evidence: {method.expected_evidence}</Typography>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
          <TextField size="small" label="Owner" value={draft[method.id]?.owner || ''} onChange={event => update(method.id, 'owner', event.target.value)} InputProps={{ readOnly: !canEdit }} />
          <TextField size="small" label="Role" value={draft[method.id]?.role || ''} onChange={event => update(method.id, 'role', event.target.value)} InputProps={{ readOnly: !canEdit }} />
          <TextField size="small" label="Due date" type="date" InputLabelProps={{ shrink: true }} value={draft[method.id]?.due_date || ''} onChange={event => update(method.id, 'due_date', event.target.value)} InputProps={{ readOnly: !canEdit }} />
        </Stack>
        <TextField fullWidth size="small" label="Acceptance criterion" sx={{ mt: 1 }} value={draft[method.id]?.acceptance_criterion || ''} onChange={event => update(method.id, 'acceptance_criterion', event.target.value)} InputProps={{ readOnly: !canEdit }} />
        {['planned', 'in_progress', 'no_longer_relevant'].includes(method.status) && <TextField select SelectProps={{ native: true }} size="small" label="Planning status" sx={{ mt: 1 }} value={draft[method.id]?.status || method.status} onChange={event => update(method.id, 'status', event.target.value as PlanningStatus)} disabled={!canEdit}>
          <option value="planned">Planned</option><option value="in_progress">In progress</option><option value="no_longer_relevant">No longer relevant</option>
        </TextField>}
        {method.evidence_references.map((reference, index) => <Typography variant="caption" display="block" key={`evidence-${index}`}>{reference}</Typography>)}
      </Box>)}
      {canEdit && <Button variant="outlined" onClick={() => void save()} disabled={!dirty}>Save plan details</Button>}
    </Stack>}
  </Box>;
};
export default MarketResearchEarlyPlan;
